import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment'
import { useGetLocations, useGetOrganizations, useWebsiteMetadata } from 'hooks'
import { getMarketingWebsiteURL } from 'utils'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import LanguageIcon from '@mui/icons-material/Language'
import CommentIcon from '@mui/icons-material/Comment'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material'
import Modal from 'features/modal/Modal'
import { SkeletonLoader } from 'components'
import SingleOrganizationDropdown from 'components/SingleOrganizationDropdown/SingleOrganizationDropdown'
import {
  Container,
  DropdownContainer,
  ContentWrapper,
  CenteredCard,
  IconCircle,
  HeaderGroup,
  Heading,
  Description,
  PrimaryButton,
  FeatureGrid,
  Feature,
  CheckIconCircle,
  FeatureTitle,
  FeatureDescription,
  WebsiteStatusTitle,
  WebsiteStatusDescription,
  WebsiteStatusInfo,
  WebsiteDateText,
  WebsiteUrlContainer,
  WebsiteLabel,
  ChilipiperEmbed,
} from './styles'

const MarketingWebsite: React.FC = () => {
  const [searchParams] = useSearchParams() 
  const { defaultLocation } = useGetLocations()
  const { defaultOrganization, setDefaultOrganizationAndUpdateURL } = useGetOrganizations()
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('')
  const { metadata, loading: isLoading, error } = useWebsiteMetadata(selectedOrganizationId)
  const [openChilipiperModal, setOpenChilipiperModal] = useState(false)
  
  const websiteUrl = !error && metadata.domainUrl
    ? (metadata.domainUrl.startsWith('http') ? metadata.domainUrl : `https://${metadata.domainUrl}`)
    : null
  
  const lastPublishedDate = metadata.lastPublishedAt
    ? moment(metadata.lastPublishedAt).format('MM/DD/YYYY')
    : null

  const handleViewWebsite = () => {
    if (websiteUrl) {
      window.open(websiteUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const handleTalkToSpecialist = () => {
    setOpenChilipiperModal(true)
  }

  // Select organization logic (extract and centralize it)
  const selectOrganization = useCallback(
    (id: string) => {
      setSelectedOrganizationId(id)
      setDefaultOrganizationAndUpdateURL(id, (orgId) => getMarketingWebsiteURL(orgId))
    },
    [setDefaultOrganizationAndUpdateURL]
  )

  // Sync organization from URL or fallback
  useEffect(() => {
    const orgIdFromURL = searchParams.get('orgId')
    const orgIdByDefaultLocation = defaultLocation?.organization_id

    // Check if there is an orgId in the URL
    if (orgIdFromURL) {
      selectOrganization(orgIdFromURL)
    } 
    // If there is no orgId, we give priority to the default location's organization
    else if (orgIdByDefaultLocation) {
      selectOrganization(String(orgIdByDefaultLocation))
    }
    // If no orgId in the URL, but there is a defaultOrganization, use it
    else if (defaultOrganization?.id) {
      selectOrganization(String(defaultOrganization.id))
    }  
  }, [searchParams, defaultLocation?.organization_id, defaultOrganization?.id, selectOrganization])

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      if (event.origin === process.env.REACT_APP_CHILIPIPER_ORIGIN) {
        if (event.data.action === 'close') {
          setOpenChilipiperModal(false)
        }
      }
    }

    window.addEventListener('message', messageHandler)
    return () => window.removeEventListener('message', messageHandler)
  }, [])

  // Handle organization change from the dropdown
  const handleOrganizationChange = (id: number) => {
    selectOrganization(String(id))
  }

  const handleCopyUrl = () => {
    if (websiteUrl) {
      navigator.clipboard.writeText(websiteUrl)
    }
  }

  return (
    <Container>
      <DropdownContainer>
        <SingleOrganizationDropdown onOrganizationChange={handleOrganizationChange} />
      </DropdownContainer>

      <ContentWrapper>
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <Card>
            <CardContent>
              <CenteredCard>
              {metadata?.hasWebsite && websiteUrl ? (
                <>
                  <WebsiteStatusTitle>Website Active</WebsiteStatusTitle>
                  <WebsiteStatusDescription>
                    Your marketing website is configured and ready for customers.
                  </WebsiteStatusDescription>
                  {lastPublishedDate && (
                    <WebsiteStatusInfo>
                      Last Published: <WebsiteDateText>{lastPublishedDate}</WebsiteDateText>
                    </WebsiteStatusInfo>
                  )}  
                  <WebsiteUrlContainer>
                    <WebsiteLabel>Website URL</WebsiteLabel>
                    <TextField
                      fullWidth
                      disabled
                      value={websiteUrl}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={handleCopyUrl}
                              edge='end'
                              aria-label='copy website url'
                              size='small'
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </WebsiteUrlContainer>
                  <PrimaryButton
                    variant='contained'
                    onClick={handleViewWebsite}
                    startIcon={<OpenInNewIcon />}
                  >
                    View Website
                  </PrimaryButton>
                </>
              ) : (
                <>
                  <IconCircle>
                    <LanguageIcon
                      sx={{
                        width: 64,
                        height: 64,
                        color: '#1B51A4',
                        fontSize: 'unset',
                      }}
                    />
                  </IconCircle>
                  <HeaderGroup>
                    <Heading>Your marketing site isn't set up yet — but we can help.</Heading>
                    <Description>
                      Launch a professional marketing website to attract new customers and grow your restaurant business.
                    </Description>
                  </HeaderGroup>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title='Schedule a Demo' arrow>
                      <PrimaryButton
                        variant='contained'
                        onClick={handleTalkToSpecialist}
                        startIcon={<CommentIcon sx={{ width: 20, height: 20 }} />}
                      >
                        Talk to a Specialist
                      </PrimaryButton>
                    </Tooltip>
                  </div>
                  <FeatureGrid>
                    <Feature>
                      <CheckIconCircle>
                        <CheckIcon sx={{ width: 24, height: 24, color: '#32965D' }} />
                      </CheckIconCircle>
                      <FeatureTitle>Professional Design</FeatureTitle>
                      <FeatureDescription>Beautiful, mobile-responsive templates designed for restaurants</FeatureDescription>
                    </Feature>
                    <Feature>
                      <CheckIconCircle>
                        <CheckIcon sx={{ width: 24, height: 24, color: '#32965D' }} />
                      </CheckIconCircle>
                      <FeatureTitle>Online Ordering</FeatureTitle>
                      <FeatureDescription>Integrated ordering system to drive direct sales</FeatureDescription>
                    </Feature>
                    <Feature>
                      <CheckIconCircle>
                        <CheckIcon sx={{ width: 24, height: 24, color: '#32965D' }} />
                      </CheckIconCircle>
                      <FeatureTitle>SEO Optimized</FeatureTitle>
                      <FeatureDescription>Built to rank well in local search results</FeatureDescription>
                    </Feature>
                  </FeatureGrid>
                </>
              )}
              </CenteredCard>
            </CardContent>
          </Card>
        )}
        <Modal
          isOpen={openChilipiperModal}
          onClose={() => setOpenChilipiperModal(false)}
          showCloseIcon={true}
          width='fit-content'
          borderRadius='1rem'
        >
          <ChilipiperEmbed
            src={process.env.REACT_APP_CHILIPIPER_CALENDAR_LINK_QUEUE}
            loading='lazy'
          />
        </Modal>
      </ContentWrapper>
    </Container>
  )
}

export default MarketingWebsite